import React from 'react';
import type { LyricTimingV1 } from 'fym-shared/src/music24/lyricTiming';
import { STUDIO_THEMES } from 'fym-shared/src/music24/studioThemes';
import { TypedMemo } from '../../../../common/types';
import type { PickLine } from '../fullVideoRules';
import { StudioDetailsProps } from './props';
import { CanvasDetails } from './CanvasDetails';
import { ShortDetails } from './ShortDetails';
import { FullVideoDetails } from './FullVideoDetails';
import { LyricMomentDetails } from './LyricMomentDetails';
import { MotionArtworkDetails } from './MotionArtworkDetails';
import type { SongLyricsView } from '../../../../smartLinks/catalog/song/page/catalogSongPath';

interface DetailsFieldsProps extends StudioDetailsProps {
  accountId: number;
  itemUuid: string;
  /** The line list of a lyric moment. */
  lyricLines: { lines: readonly PickLine[]; timing: LyricTimingV1 | null; isLoading: boolean };
  onOpenSongLyrics: (view?: SongLyricsView) => void;
}

/**
 * The fields of step 3 for one asset type (an image has its own `ImageDetails`): the Canvas window, the short video
 * fields, the length and lyrics of a full video (s7), the lines of a lyric moment (s7b) and the
 * length of the motion artwork.
 */
const DetailsFieldsComponent: React.FC<DetailsFieldsProps> = (props) => {
  const { accountId, itemUuid, lyricLines, onOpenSongLyrics, themeId, details, song, onChange } = props;
  const theme = STUDIO_THEMES[themeId];

  if (theme.format === 'image') {
    // An image has its own tiles (`ImageDetails`); `DetailsStep` draws them with the previews.
    return null;
  }
  if (theme.format === 'full') {
    return (
      <FullVideoDetails
        themeId={themeId}
        details={details}
        song={song}
        onChange={onChange}
        onOpenSongLyrics={onOpenSongLyrics}
      />
    );
  }
  if (themeId === 'canvasLoop') {
    return <CanvasDetails themeId={themeId} details={details} song={song} onChange={onChange} />;
  }
  if (themeId === 'lyricMoment') {
    return (
      <LyricMomentDetails
        themeId={themeId}
        details={details}
        song={song}
        onChange={onChange}
        accountId={accountId}
        itemUuid={itemUuid}
        lines={lyricLines.lines}
        timing={lyricLines.timing}
        isLoading={lyricLines.isLoading}
        onOpenSongLyrics={onOpenSongLyrics}
      />
    );
  }
  if (themeId === 'motionArtwork') {
    return <MotionArtworkDetails themeId={themeId} details={details} song={song} onChange={onChange} />;
  }
  return <ShortDetails themeId={themeId} details={details} song={song} onChange={onChange} />;
};

export const DetailsFields = TypedMemo(DetailsFieldsComponent);
